import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import debounce from "lodash/debounce";
import {ModalAction} from "@vedmarket/ui-kit";

import ModalBig from "components/common/modalBig";
import ShowMoreMenu from "components/common/showMoreMenu";

import { Add as AddIcon } from '@vedmarket/ui-kit';
import { Check as CheckIcon } from '@vedmarket/ui-kit';
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";

import {useGetSuppliersMutation, useRemoveSupplierMutation} from "../../../../../../../../../../../../../api/dictionary";
import Preloader from "../../../../../../../../../../../../common/preloader";
import {COLORS} from "../../../../../../../../../../../../../config/constants";
import {RESULTS_COUNT} from "../../../../../../../../../../../../../utils/constants";
import TablePaginationActions from "../../../products_table/components/table/components/TablePaginationActions";

import SupplierSettingsPanel, {ACTIVE_COLUMNS_BY_DEFAULT} from "../supplierSettingsPanel";
import SupplierSearchField from "../supplierSearchField";

import "./supplierSelectModal.scss";

const INITIAL_SUPPLIERS_REQUEST: DictionaryParamsInput = {
  limit: RESULTS_COUNT.COUNT_10,
  ordering: "name",
  page: 1,
  search: "",
};

const getSupplierContactText = (supplier: SupplierDictionaryOutput) =>
  [
    supplier.legal_address,
    supplier.phone,
    supplier.contact_person,
  ]
    .filter(Boolean)
    .join(",\n");

const SupplierSelectModal = ({
  addedSupplierId,
  isOpen,
  refreshId,
  selectedSupplierId,
  suppliers,
  onClose,
  onAddSupplier,
  onDeleteSupplier,
  onEditSupplier,
  onChooseSupplier,
  onSelectSupplier,
}: SupplierSelectModalProps) => {

  const { t } = useTranslation()

  const [ activeColumns, setActiveColumns ] = useState<string[]>(ACTIVE_COLUMNS_BY_DEFAULT)
  const [ supplierToDelete, setSupplierToDelete ] = useState<SupplierDictionaryOutput | undefined>()
  const [ hasRequestedSuppliers, setHasRequestedSuppliers ] = useState(false)
  const [ searchValue, setSearchValue ] = useState("")
  const [ suppliersTotalCount, setSuppliersTotalCount ] = useState(0)
  const [ suppliersListParams, setSuppliersListParams ] = useState<DictionaryParamsInput>(INITIAL_SUPPLIERS_REQUEST)

  const [ suppliersRequest, { data: suppliersList, isLoading: isLoadingSuppliersListRequest } ] = useGetSuppliersMutation()

  const [ removeSupplier ] = useRemoveSupplierMutation()

  const refreshIdRef = useRef(refreshId);

  const suppliersListParamsRef = useRef(INITIAL_SUPPLIERS_REQUEST);

  const suppliersListRequestUpdate = async (params?: DictionaryParamsInput) => {
    const nextParams = { ...suppliersListParamsRef.current, ...params };

    setHasRequestedSuppliers(true);
    suppliersListParamsRef.current = nextParams;
    setSuppliersListParams(nextParams);

    const suppliersResponse = await suppliersRequest(nextParams)
      .unwrap()
      .catch((): SuppliersResponse => ({ results: [] }));

    const totalCount = suppliersResponse.count || suppliersResponse.results.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / (nextParams.limit || RESULTS_COUNT.COUNT_10)));

    setSuppliersTotalCount(totalCount);

    if ((nextParams.page || 1) > totalPages) {
      const correctParams = {
        ...nextParams,
        page: totalPages,
      };

      suppliersListParamsRef.current = correctParams;
      setSuppliersListParams(correctParams);

      const correctedSuppliers = await suppliersRequest(correctParams)
        .unwrap()
        .catch((): SuppliersResponse => ({ results: [] }));

      setSuppliersTotalCount(correctedSuppliers.count || correctedSuppliers.results.length);
    }
  }

  const debouncedSearchRequest = useRef(
    debounce((query: string) => {
      suppliersListRequestUpdate({
        page: 1,
        search: query,
      });
    }, 1000)
  ).current

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setHasRequestedSuppliers(false);
    setSearchValue("");
    setActiveColumns(ACTIVE_COLUMNS_BY_DEFAULT);
    setSuppliersTotalCount(suppliers.length);
    suppliersListParamsRef.current = INITIAL_SUPPLIERS_REQUEST;
    setSuppliersListParams(INITIAL_SUPPLIERS_REQUEST);
    refreshIdRef.current = refreshId;
  }, [ isOpen ])

  useEffect(() => {
    return () => {
      debouncedSearchRequest.cancel();
    };
  }, []);

  useEffect(() => {
    if (!isOpen || refreshId === refreshIdRef.current) {
      return;
    }

    refreshIdRef.current = refreshId;

    if (!hasRequestedSuppliers) {
      setSuppliersTotalCount(suppliers.length);
      return;
    }

    suppliersListRequestUpdate();
  }, [ hasRequestedSuppliers, isOpen, refreshId, suppliers.length ])

  const handleChangeColumn = (id: string): void => {
    setActiveColumns((prevColumns: string[]) =>
      prevColumns.includes(id)
        ? prevColumns.filter((column) => column !== id)
        : [ ...prevColumns, id ]
    );
  }

  const handleSearch = (value: string) => {
    setSearchValue(value);
    debouncedSearchRequest(value);
  }

  const handleClearSearch = () => {
    debouncedSearchRequest.cancel();
    setSearchValue("");
    suppliersListRequestUpdate({
      page: 1,
      search: "",
    });
  }

  const ordering = suppliersListParams.ordering || "name";
  const rowsPerPage = suppliersListParams.limit || RESULTS_COUNT.COUNT_10;
  const currentPage = suppliersListParams.page || 1;

  const initialVisibleSuppliers = hasRequestedSuppliers
    ? suppliersList?.results || suppliers.slice(0, rowsPerPage)
    : suppliers.slice(0, rowsPerPage);

  const visibleSuppliers = addedSupplierId
    ? [
      ...(suppliers.filter((supplier) => supplier.id === addedSupplierId)),
      ...initialVisibleSuppliers.filter((supplier) => supplier.id !== addedSupplierId)
    ].slice(0, rowsPerPage)
    : initialVisibleSuppliers;

  const getSupplierId = (index: number) => (currentPage - 1) * rowsPerPage + index + 1;

  const handleDeleteSupplier = async (supplierId: number) => {
    await removeSupplier(supplierId).unwrap();
    await onDeleteSupplier(supplierId);
    await suppliersListRequestUpdate();
  }

  const handleOpenDeleteModal = (supplier: SupplierDictionaryOutput) => {
    setSupplierToDelete(supplier);
  }

  const handleCloseDeleteModal = () => {
    setSupplierToDelete(undefined);
  }

  const handleConfirmDeleteSupplier = async () => {
    if (!supplierToDelete) {
      return;
    }

    await handleDeleteSupplier(supplierToDelete.id);
    handleCloseDeleteModal();
  }

  const deleteSupplierDescription = <Typography variant='body1'>
    {t("calc__text__main__removeSupplierQuestion")}{" "}
    <Typography
      component='span'
      variant='body1'
      sx={{ fontWeight: 700 }}
    >
      {supplierToDelete?.name || ""}
    </Typography>
    ?
    <br />
    {t("calc__text__main__removeSupplierDescription")}
  </Typography>

  const getShowMoreMenu = (supplier: SupplierDictionaryOutput) => <div
    className='supplier-select-modal__settings-value'
    onClick={(event) => event.stopPropagation()}
  >
    <ShowMoreMenu menuItems={[
      {
        title: t("actions__text__edit"),
        onClick: () => onEditSupplier(supplier)
      },
      {
        title: t("actions__text__delete"),
        onClick: () => handleOpenDeleteModal(supplier)
      },
    ]}/>
  </div>;

  const rows: SupplierSelectModalRow[] = visibleSuppliers.map((supplier, index) => ({
    ...supplier,
    contact: getSupplierContactText(supplier),
    id: getSupplierId(index),
    supplier_id: supplier.id,
    settings: getShowMoreMenu(supplier)
  }));

  const getHeadCells = (): SupplierSelectModalHeadCell[] => {
    const allColumns: SupplierSelectModalHeadCell[] = [
      {
        id: "id",
        numeric: false,
        disablePadding: true,
        label: "№",
        sortable: false,
      },
      {
        id: "name",
        numeric: false,
        disablePadding: true,
        label: t("calc__text__main__supplierCompany"),
        sortable: true,
      },
      {
        id: "contract_num",
        numeric: false,
        disablePadding: true,
        label: t("calc__text__main__contractNumber"),
        sortable: true,
      },
      {
        id: "contract_date",
        numeric: false,
        disablePadding: true,
        label: t("calc__text__main__contractDate"),
        sortable: true,
      },
      {
        id: "contact",
        numeric: false,
        disablePadding: true,
        label: t("calc__text__main__addressPhoneContactPerson"),
        sortable: false,
      },
      {
        id: "additional_info",
        numeric: false,
        disablePadding: true,
        label: t("calc__text__main__comments"),
        sortable: true,
      },
      {
        id: "settings",
        numeric: false,
        disablePadding: true,
        label: <SupplierSettingsPanel
          handleSelect={handleChangeColumn}
          activeColumns={activeColumns}
          setActiveColumns={setActiveColumns}
        />,
        sortable: false,
      },
    ];

    return allColumns.filter((column) => activeColumns.includes(column.id));
  }

  const getPreferences = (): SupplierSelectModalPreference[] => {
    const allColumns: SupplierSelectModalPreference[] = [
      {
        id: "id",
        width: 32,
      },
      {
        id: "name",
        width: 140,
      },
      {
        id: "contract_num",
        width: 120,
      },
      {
        id: "contract_date",
        width: 120,
      },
      {
        id: "contact",
        width: 120,
      },
      {
        id: "additional_info",
        width: 120,
      },
      {
        id: "settings",
        width: 48,
      },
    ];

    return allColumns.filter((column) => activeColumns.includes(column.id));
  }

  const headCells = getHeadCells();
  const preferences = getPreferences();
  const shouldShowPagination = suppliersTotalCount >= RESULTS_COUNT.COUNT_10;

  const getArrowDirection = (columnId: SupplierSelectModalRowKey) => {
    if (ordering.includes(columnId)){
      return columnId === ordering ? "desc" : "asc";
    }

    return undefined;
  }

  const getHeadCellAlign = (headCell: SupplierSelectModalHeadCell) => {
    if (headCell.id === "settings") {
      return "center";
    }

    if (headCell.numeric) {
      return "right";
    }

    return "left";
  }

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    suppliersListRequestUpdate({
      ordering: property === ordering ? `-${property}` : property,
    });
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    suppliersListRequestUpdate({
      page: newPage + 1
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const limit = parseInt(event.target.value, 10);

    suppliersListRequestUpdate({
      limit,
      page: 1,
    });
  };

  const isLoadingStatus = isLoadingSuppliersListRequest && !visibleSuppliers.length;

  return <>
    <ModalBig
      isOpen={isOpen}
      onClose={onClose}
      title={t("calc__text__main__chooseSupplierCompany")}
      width={980}
      noPadding
      customStyles={{ minWidth: 980 }}
      buttons={<div className='supplier-select-modal__actions'>
        <Button
          variant='outlined'
          onClick={onClose}
          sx={{
            width: '258px',
          }}
          className='supplier-select-modal__actions-button supplier-select-modal__actions-button_cancel'
        >{t("actions__text__cancel")}</Button>
        <Button
          variant='contained'
          startIcon={<CheckIcon />}
          sx={{
            width: '454px',
          }}
          disabled={!selectedSupplierId}
          onClick={() => selectedSupplierId && onChooseSupplier(selectedSupplierId)}
          className='supplier-select-modal__actions-button supplier-select-modal__actions-button_choose'
        >{t("calc__text__main__chooseSupplierCompanyAction")}</Button>
      </div>}
    >
      <div className='supplier-select-modal'>
        {isLoadingStatus ? <div className='supplier-select-modal__preloader'>
          <Preloader />
        </div> : <>
          <div className='supplier-select-modal__search'>
            <SupplierSearchField
              value={searchValue}
              onChange={handleSearch}
              onClear={handleClearSearch}
            />
          </div>
          <TableContainer
            className='supplier-select-modal__table-container'
            sx={{
              borderTop: "1px solid",
              borderTopColor: COLORS.SLIGHTLY_GRAY,
              boxShadow: "none",
            }}
          >
            <Table className='supplier-select-modal__table'>
              <TableHead>
                <TableRow>
                  <TableCell
                    padding='checkbox'
                    className='supplier-select-modal__radio-head'
                    sx={{ padding: "0", width: 34 }}
                  />
                  {headCells.map((headCell, index: number) => (
                    <TableCell
                      key={headCell.id}
                      align={getHeadCellAlign(headCell)}
                      padding={headCell.disablePadding ? "none" : "normal"}
                      sortDirection={getArrowDirection(headCell.id)}
                      style={{ width: preferences[index].width }}
                      className='supplier-select-modal__head-cell'
                      sx={{
                        fontWeight: 500,
                        padding: "12px 0 12px 0",
                        textAlign: headCell.id === "settings" ? "center" : "left",
                      }}
                    >
                      <TableSortLabel
                        active={Boolean(headCell.sortable && getArrowDirection(headCell.id))}
                        direction={getArrowDirection(headCell.id) || "asc"}
                        sx={{
                          ".MuiTableSortLabel-icon": {
                            display: headCell.sortable ? "inline-block" : "none",
                          },
                          justifyContent: headCell.id === "settings" ? "center" : "flex-start",
                          fontWeight: "500",
                          width: "100%",
                        }}
                        onClick={(event) => headCell.sortable && handleRequestSort(event, headCell.id)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length ? rows.map((row) => {
                  const isSelected = row.supplier_id === selectedSupplierId;

                  return <TableRow
                    hover
                    key={row.supplier_id}
                    onClick={() => onSelectSupplier(row.supplier_id)}
                    selected={isSelected}
                    className='supplier-select-modal__row'
                    sx={{
                      cursor: "pointer",
                      "&.Mui-selected": {
                        background: "rgba(25, 118, 210, 0.08)",
                      },
                    }}
                  >
                    <TableCell
                      padding='checkbox'
                      className='supplier-select-modal__radio-cell'
                      sx={{
                        padding: "0",
                        verticalAlign: "top",
                      }}
                    >
                      <Radio
                        checked={isSelected}
                        onChange={() => onSelectSupplier(row.supplier_id)}
                        value={row.supplier_id}
                        sx={{
                          margin: '4px 0 0'
                        }}
                      />
                    </TableCell>
                    {preferences.map((preference) => {
                      const isSettingsColumn = preference.id === "settings";
                      const rowValue = row[preference.id];

                      return <TableCell
                        component='th'
                        scope='row'
                        style={{ width: preference.width }}
                        key={preference.id}
                        className='supplier-select-modal__cell'
                        sx={{
                          lineHeight: 1.45,
                          padding: isSettingsColumn ? "16px 0" : "16px 16px 16px 0",
                          textAlign: isSettingsColumn ? "center" : "left",
                          verticalAlign: "top",
                          wordBreak: "break-word",
                        }}
                      >
                        {isSettingsColumn
                          ? rowValue
                          : <span className='supplier-select-modal__multiline'>
                            {rowValue || rowValue === 0 ? rowValue : "-"}
                          </span>}
                      </TableCell>
                    })}
                  </TableRow>
                }) : <TableRow>
                  <TableCell
                    colSpan={preferences.length + 1}
                    className='supplier-select-modal__empty'
                    sx={{
                      padding: "40px 0",
                      textAlign: "center",
                    }}
                  >
                    {t("calc__text__main__noOptionsFound")}
                  </TableCell>
                </TableRow>}
              </TableBody>
              {shouldShowPagination && <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[ RESULTS_COUNT.COUNT_10, RESULTS_COUNT.COUNT_20, RESULTS_COUNT.COUNT_50 ]}
                    colSpan={preferences.length + 1}
                    count={suppliersTotalCount}
                    rowsPerPage={rowsPerPage}
                    page={currentPage - 1}
                    labelRowsPerPage={t("common__text__rowsCount")}
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t("common__text__of")} ${count}`}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>}
            </Table>
          </TableContainer>
          <Typography
            variant='body1'
            className='supplier-select-modal__add'
            onClick={onAddSupplier}
            sx={{
              color: COLORS.BLUE,
              marginTop: 2
            }}
          >
            <AddIcon style={{ marginRight: '8px' }}/>
            {t("calc__text__main__addSupplierCompanyAction")}
          </Typography>
        </>}
      </div>
    </ModalBig>
    <ModalAction
      isOpen={Boolean(supplierToDelete)}
      onClose={handleCloseDeleteModal}
      onContinue={handleConfirmDeleteSupplier}
      title={t("calc__text__main__removeSupplierTitle")}
      description={deleteSupplierDescription}
      buttonYesText={t("actions__text__delete")}
    />
  </>
}

export default SupplierSelectModal
